import { ctor } from 'from2';
import { async } from 'regenerator-runtime';
import { req, breq, imgServer, getWebsiteData } from '../js/config';
import { preloader, withPreloader } from '../js/preloader';
import { currencyFormatter } from '../js/base';

let currentStep = 1;
const totalSteps = 6;
let inventoryData = [];
const formData = {};

const Initialize = async () => {
  // Initialize preloader manager with timeout protection
  preloader.init();

  try {
    // Wrap initialization with timeout protection
    await withPreloader(
      async () => {
        await initializePage();
      },
      { timeout: 5000 }
    ); // 5 second timeout for initialization

    // Hide preloader on success
    preloader.hide();
  } catch (err) {
    console.error('Error during initialization:', err);
    // Preloader will be automatically hidden by the withPreloader function
  }
};

const initializePage = async () => {
  // Load inventory data for matching
  try {
    const webData = await getWebsiteData();
    inventoryData = webData.filter((x) => x.attributes.Status === 'Available');
    console.log('Loaded inventory data:', inventoryData.length, 'vehicles');
  } catch (error) {
    console.error('Error loading inventory data:', error);
    inventoryData = [];
  }

  // Initialize header and footer business information (same as inventory page)
  await generatePageHTML();

  // Initialize form functionality
  setupBudgetSliders();
  setupFeatureSelection();
  setupFormSubmission();
  updateStepIndicator();

  // Hide previous button on first step
  document.getElementById('prevBtn').style.display = 'none';

  // Set hidden form values
  document.getElementById('submissionDate').value = new Date().toISOString();
  document.getElementById('referrer').value = document.referrer || 'Direct';
};

const setupBudgetSliders = () => {
  const minBudgetSlider = document.getElementById('minBudget');
  const maxBudgetSlider = document.getElementById('maxBudget');
  const budgetDisplay = document.getElementById('budgetDisplay');

  const updateBudgetDisplay = () => {
    const minBudget = parseInt(minBudgetSlider.value);
    const maxBudget = parseInt(maxBudgetSlider.value);

    // Ensure min doesn't exceed max
    if (minBudget >= maxBudget) {
      minBudgetSlider.value = maxBudget - 1000;
    }

    // Ensure max doesn't go below min
    if (maxBudget <= minBudget) {
      maxBudgetSlider.value = minBudget + 1000;
    }

    const displayMin = parseInt(minBudgetSlider.value);
    const displayMax = parseInt(maxBudgetSlider.value);

    budgetDisplay.textContent = `$${displayMin.toLocaleString()} - $${
      displayMax >= 80000 ? '80,000+' : displayMax.toLocaleString()
    }`;
  };

  minBudgetSlider.addEventListener('input', updateBudgetDisplay);
  maxBudgetSlider.addEventListener('input', updateBudgetDisplay);

  // Initial update
  updateBudgetDisplay();
};

const setupFeatureSelection = () => {
  // Wait for DOM to be fully loaded
  setTimeout(() => {
    // Handle body type selection (step 2) - Fixed: More robust selection
    const bodyTypeItems = document.querySelectorAll(
      '.form-step[data-step="2"] .feature-item'
    );
    console.log('Body type items found:', bodyTypeItems.length);

    bodyTypeItems.forEach((item) => {
      // Remove any existing listeners first
      item.removeEventListener('click', handleBodyTypeClick);
      // Add new listener
      item.addEventListener('click', handleBodyTypeClick);
    });

    // Handle feature selection (step 4)
    const featureItems = document.querySelectorAll(
      '.form-step[data-step="4"] .feature-item'
    );
    console.log('Feature items found:', featureItems.length);

    featureItems.forEach((item) => {
      item.removeEventListener('click', handleFeatureClick);
      item.addEventListener('click', handleFeatureClick);
    });

    // Handle timeline selection (step 5)
    const timelineItems = document.querySelectorAll(
      '.form-step[data-step="5"] .feature-item'
    );
    console.log('Timeline items found:', timelineItems.length);

    timelineItems.forEach((item) => {
      item.removeEventListener('click', handleTimelineClick);
      item.addEventListener('click', handleTimelineClick);
    });
  }, 100);
};

// Fixed: Separate event handlers to prevent issues
const handleBodyTypeClick = (e) => {
  e.preventDefault();
  e.stopPropagation();
  const item = e.currentTarget;
  console.log('Body type item clicked:', item.getAttribute('data-value'));
  item.classList.toggle('selected');
  updateHiddenInput(
    'bodyTypes',
    document.querySelectorAll('.form-step[data-step="2"] .feature-item')
  );
};

const handleFeatureClick = (e) => {
  e.preventDefault();
  e.stopPropagation();
  const item = e.currentTarget;
  console.log('Feature item clicked:', item.getAttribute('data-value'));
  item.classList.toggle('selected');
  updateHiddenInput(
    'desiredFeatures',
    document.querySelectorAll('.form-step[data-step="4"] .feature-item')
  );
};

const handleTimelineClick = (e) => {
  e.preventDefault();
  e.stopPropagation();
  const item = e.currentTarget;
  console.log('Timeline item clicked:', item.getAttribute('data-value'));

  // Only allow single selection for timeline
  document
    .querySelectorAll('.form-step[data-step="5"] .feature-item')
    .forEach((t) => t.classList.remove('selected'));
  item.classList.add('selected');
  document.getElementById('timeline').value = item.getAttribute('data-value');
};

const updateHiddenInput = (inputId, items) => {
  const selectedValues = [];
  items.forEach((item) => {
    if (item.classList.contains('selected')) {
      selectedValues.push(item.getAttribute('data-value'));
    }
  });
  const hiddenInput = document.getElementById(inputId);
  if (hiddenInput) {
    hiddenInput.value = selectedValues.join(',');
    console.log(`Updated ${inputId}:`, selectedValues.join(','));
  } else {
    console.error(`Hidden input ${inputId} not found`);
  }
};

const setupFormSubmission = () => {
  const form = document.getElementById('carFinderForm');
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    await submitForm();
  });
};

// Global function for navigation buttons
window.changeStep = (direction) => {
  const newStep = currentStep + direction;

  if (newStep < 1 || newStep > totalSteps) {
    return;
  }

  // Validate current step before proceeding
  if (direction > 0 && !validateCurrentStep()) {
    return;
  }

  // Hide current step (both indicator and form step)
  document
    .querySelector(`.step[data-step="${currentStep}"]`)
    .classList.remove('active');
  document
    .querySelector(`.form-step[data-step="${currentStep}"]`)
    .classList.remove('active');

  // Update step
  currentStep = newStep;

  // Show new step (both indicator and form step)
  document
    .querySelector(`.step[data-step="${currentStep}"]`)
    .classList.add('active');
  document
    .querySelector(`.form-step[data-step="${currentStep}"]`)
    .classList.add('active');

  // Update progress and navigation
  updateProgress();
  updateStepIndicator();
  updateNavigationButtons();

  // Scroll to top of form
  document.querySelector('.finder-form').scrollIntoView({ behavior: 'smooth' });

  // Show matching inventory if we're on step 3 or later
  if (currentStep >= 3) {
    showMatchingInventory();
  }
};

const validateCurrentStep = () => {
  switch (currentStep) {
    case 1:
      // Budget validation
      const minBudget = parseInt(document.getElementById('minBudget').value);
      const maxBudget = parseInt(document.getElementById('maxBudget').value);
      if (minBudget >= maxBudget) {
        alert(
          'Please ensure your maximum budget is higher than your minimum budget.'
        );
        return false;
      }
      break;
    case 2:
      // Body type validation - Fixed: Check for selected items properly
      const selectedBodyTypes = document.querySelectorAll(
        '[data-step="2"] .feature-item.selected'
      );
      console.log(
        'Validating body types, found selected:',
        selectedBodyTypes.length
      );
      if (selectedBodyTypes.length === 0) {
        alert('Please select at least one vehicle type.');
        return false;
      }
      break;
    case 5:
      // Timeline validation
      const timeline = document.getElementById('timeline').value;
      if (!timeline) {
        alert('Please select your timeline.');
        return false;
      }
      break;
    case 6:
      // Contact info validation
      const requiredFields = ['fullName', 'email', 'phone'];
      for (const field of requiredFields) {
        const value = document.getElementById(field).value.trim();
        if (!value) {
          alert(
            `Please fill in your ${field
              .replace(/([A-Z])/g, ' $1')
              .toLowerCase()}.`
          );
          document.getElementById(field).focus();
          return false;
        }
      }

      // Email validation
      const email = document.getElementById('email').value;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        alert('Please enter a valid email address.');
        document.getElementById('email').focus();
        return false;
      }
      break;
  }
  return true;
};

const updateProgress = () => {
  const progressFill = document.getElementById('progressFill');
  const progressPercent = (currentStep / totalSteps) * 100;
  progressFill.style.width = `${progressPercent}%`;
};

const updateStepIndicator = () => {
  // Update step indicators
  document.querySelectorAll('.step').forEach((step, index) => {
    if (index + 1 <= currentStep) {
      step.classList.add('active');
    } else {
      step.classList.remove('active');
    }
  });
};

const updateNavigationButtons = () => {
  const prevBtn = document.getElementById('prevBtn');
  const nextBtn = document.getElementById('nextBtn');

  // Show/hide previous button
  if (currentStep === 1) {
    prevBtn.style.display = 'none';
  } else {
    prevBtn.style.display = 'inline-block';
  }

  // Update next button text for last step
  if (currentStep === totalSteps) {
    nextBtn.innerHTML = 'Submit Request <i class="ion-checkmark-circled"></i>';
    nextBtn.onclick = submitForm;
  } else {
    nextBtn.innerHTML = 'Next <i class="ion-arrow-right-c"></i>';
    nextBtn.onclick = () => changeStep(1);
  }
};

const collectFormData = () => {
  const formElement = document.getElementById('carFinderForm');
  const formData = new FormData(formElement);
  const data = {};

  // Get all form fields
  for (let [key, value] of formData.entries()) {
    data[key] = value;
  }

  // Get preferred makes (multi-select)
  const preferredMakes = Array.from(
    document.getElementById('preferredMakes').selectedOptions
  ).map((option) => option.value);
  data.preferredMakes = preferredMakes;

  // Get checkboxes
  data.needFinancing = document.getElementById('needFinancing').checked;
  data.haveTradeIn = document.getElementById('haveTradeIn').checked;
  data.newsletterSignup = document.getElementById('newsletterSignup').checked;

  console.log('Collected form data:', data);
  return data;
};

const generateEmailContent = (data) => {
  const bodyTypes = data.bodyTypes
    ? data.bodyTypes.split(',').join(', ')
    : 'Any';
  const makes =
    data.preferredMakes.length > 0 ? data.preferredMakes.join(', ') : 'Any';
  const features = data.desiredFeatures
    ? data.desiredFeatures.split(',').join(', ')
    : 'None specified';

  return `
🚗 NEW CAR FINDER REQUEST 🚗

CUSTOMER INFORMATION:
Name: ${data.fullName}
Email: ${data.email}
Phone: ${data.phone}
Preferred Contact: ${data.contactPreference}
Best Time: ${data.bestTimeToContact}
Timeline: ${data.timeline}

VEHICLE REQUIREMENTS:
Budget: $${parseInt(data.minBudget).toLocaleString()} - $${parseInt(
    data.maxBudget
  ).toLocaleString()}
Body Types: ${bodyTypes}
Preferred Makes: ${makes}
Year Range: ${data.yearRange || 'Any'}
Max Mileage: ${
    data.maxMileage ? parseInt(data.maxMileage).toLocaleString() + ' km' : 'Any'
  }
Transmission: ${data.transmission || 'Any'}

DESIRED FEATURES:
${features}

FINANCING & TRADE:
Needs Financing: ${data.needFinancing ? 'Yes' : 'No'}
Has Trade-in: ${data.haveTradeIn ? 'Yes' : 'No'}

ADDITIONAL NOTES:
${data.additionalNotes || 'None'}

MARKETING INSIGHTS:
Newsletter Signup: ${data.newsletterSignup ? 'Yes' : 'No'}
Referrer: ${data.referrer}
Submission Date: ${new Date(data.submissionDate).toLocaleString()}

---
This is a high-priority lead from the Car Finder Assistant. Please follow up within 48 hours.
  `;
};

const showSuccessMessage = () => {
  // Hide the form
  document.getElementById('carFinderForm').style.display = 'none';
  document.querySelector('.step-indicator').style.display = 'none';
  document.querySelector('.progress-bar-custom').style.display = 'none';
  document.querySelector('.btn-navigation').style.display = 'none';

  // Show success message
  document.getElementById('successMessage').style.display = 'block';

  // Scroll to success message
  document
    .getElementById('successMessage')
    .scrollIntoView({ behavior: 'smooth' });
};

const showMatchingInventory = () => {
  if (inventoryData.length === 0) {
    console.log('No inventory data available for matching');
    return;
  }

  const currentData = collectFormData();
  const minBudget = parseInt(currentData.minBudget) || 0;
  const maxBudget = parseInt(currentData.maxBudget) || 999999;
  const selectedBodyTypes = currentData.bodyTypes
    ? currentData.bodyTypes.split(',')
    : [];

  console.log('Matching criteria:', {
    minBudget,
    maxBudget,
    selectedBodyTypes,
  });

  // Filter inventory based on current form data
  let matchingVehicles = inventoryData.filter((vehicle) => {
    const price = vehicle.attributes.Price || 0;
    const category = vehicle.attributes.Category || '';

    // Price filter
    const priceMatch = price >= minBudget && price <= maxBudget;

    // Body type filter (if any selected)
    let bodyTypeMatch = true;
    if (selectedBodyTypes.length > 0) {
      bodyTypeMatch = selectedBodyTypes.some(
        (type) =>
          category.toLowerCase().includes(type.toLowerCase()) ||
          type.toLowerCase().includes(category.toLowerCase())
      );
    }

    return priceMatch && bodyTypeMatch;
  });

  console.log('Found matching vehicles:', matchingVehicles.length);

  // Show matching vehicles section
  displayMatchingVehicles(matchingVehicles.slice(0, 6)); // Show up to 6 matches
};

const displayMatchingVehicles = (vehicles) => {
  // Create or find the matching vehicles section
  let matchingSection = document.getElementById('matchingVehiclesSection');
  if (!matchingSection) {
    matchingSection = document.createElement('div');
    matchingSection.id = 'matchingVehiclesSection';
    matchingSection.className =
      'matching-vehicles-section mt-4 p-4 bg-light rounded';

    // Insert after the form
    const form = document.getElementById('carFinderForm');
    form.parentNode.insertBefore(matchingSection, form.nextSibling);
  }

  if (vehicles.length === 0) {
    matchingSection.innerHTML = `
      <div class="text-center">
        <h4 class="text-muted">No vehicles currently match your criteria</h4>
        <p class="text-muted">Don't worry! Submit your request and we'll find the perfect car for you.</p>
      </div>`;
    return;
  }

  let html = `
    <div class="matching-header text-center mb-4">
      <h4><i class="ion-checkmark-circled text-success"></i> Found ${
        vehicles.length
      } Matching Vehicle${vehicles.length > 1 ? 's' : ''} in Our Inventory!</h4>
      <p class="text-muted">These vehicles match your current criteria. Click to view details.</p>
    </div>
    <div class="row">
  `;

  vehicles.forEach((vehicle) => {
    const car = vehicle.attributes;
    const carInfo = `${car.Year} ${car.Make} ${car.Model}`;
    const imageUrl = car.Images?.data?.[0]?.attributes?.formats?.small?.url;
    const fullImageUrl = imageUrl
      ? imgServer + imageUrl
      : '/assets/images/no-image.jpg';

    html += `
      <div class="col-md-6 col-lg-4 mb-3">
        <div class="matching-vehicle-card h-100">
          <div class="card">
            <img src="${fullImageUrl}" class="card-img-top" alt="${carInfo}" style="height: 200px; object-fit: cover;">
            <div class="card-body">
              <h6 class="card-title">${carInfo}</h6>
              <p class="price-tag">${currencyFormatter.format(car.Price)}</p>
              <a href="cardetail.html?id=${
                vehicle.id
              }&location=brampton&used-car-for-sale=${encodeURIComponent(
      carInfo
    )}" 
                 class="btn btn-primary btn-sm w-100">View Details</a>
            </div>
          </div>
        </div>
      </div>
    `;
  });

  html += `
    </div>
    <div class="text-center mt-3">
      <a href="inventory.html" class="btn btn-outline-primary">View All Inventory</a>
    </div>
  `;

  matchingSection.innerHTML = html;
};

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', Initialize);

// Also initialize on module load as backup
Initialize();

// Header and Footer Business Information Functions (same as inventory page)
const generatePageHTML = async () => {
  try {
    const copyrightFooter = document.querySelector('#copyrightYearsFooter');
    const copyrightMobile = document.querySelector('#copyrightYearsMobileMenu');

    if (copyrightFooter) {
      copyrightFooter.innerHTML =
        '&copy;' +
        new Date().getFullYear() +
        ' <span> Hansen Truck & Car Sales</span>';
    }

    if (copyrightMobile) {
      copyrightMobile.innerHTML =
        '&copy;' +
        new Date().getFullYear() +
        ' <span> Hansen Truck & Car Sales</span>';
    }

    try {
      let businessData = await googlePlaceData();
      if (
        businessData != null &&
        businessData.opening_hours &&
        businessData.formatted_phone_number &&
        businessData.formatted_address
      ) {
        await updateCurrentDayHours(businessData.opening_hours);
        await phoneNumber(businessData.formatted_phone_number);
        await address(businessData.formatted_address);
        console.log('✅ Business information loaded from Google Places API');
      } else {
        console.warn('⚠️ Google Places data incomplete, using fallback');
        setDefaultBusinessInfo();
      }
    } catch (googleErr) {
      console.warn(
        '⚠️ Error getting Google Place data, using fallback:',
        googleErr
      );
      setDefaultBusinessInfo();
    }
  } catch (err) {
    console.error('Error in generatePageHTML:', err);
    // Even if there's an error, try to set the fallback info
    setDefaultBusinessInfo();
  }
};

const updateCurrentDayHours = async (h) => {
  try {
    if (!h || !h.weekday_text || !Array.isArray(h.weekday_text)) {
      console.warn('Invalid hours data provided to updateCurrentDayHours');
      return;
    }

    let currentDay = new Date().getDay();
    currentDay = currentDay === 0 ? (currentDay = 7) : currentDay;

    const currentDayElement = document.querySelector('#currentDayHours');
    if (currentDayElement && h.weekday_text[currentDay - 1]) {
      currentDayElement.innerHTML = h.weekday_text[currentDay - 1];
    }

    let tpa = '';
    for (let i = 0; i < h.weekday_text.length; i++) {
      if (h.weekday_text[i]) {
        tpa += `<li>${h.weekday_text[i]}</li>`;
      }
    }
    const workingHoursElement = document.querySelector('#workingHours');
    if (workingHoursElement && tpa) {
      workingHoursElement.innerHTML = tpa;
    }
  } catch (err) {
    console.error('Error in updateCurrentDayHours:', err);
  }
};

const phoneNumber = async (p) => {
  try {
    if (!p || typeof p !== 'string') {
      console.warn('Invalid phone number provided');
      return;
    }

    const phoneNumberElement = document.querySelector('#phoneNumber');
    const footerPhoneElement = document.querySelector('#footerPhoneNumber');

    const cleanPhone = p.replace(/[\s\-\(\)]+/g, '');
    const phoneHTML = `<a href="tel:+${cleanPhone}">${p}</a>`;

    if (phoneNumberElement) {
      phoneNumberElement.innerHTML = phoneHTML;
    }
    if (footerPhoneElement) {
      footerPhoneElement.innerHTML = phoneHTML;
    }
  } catch (err) {
    console.error('Error in phoneNumber function:', err);
  }
};

const address = async (a) => {
  try {
    if (!a || typeof a !== 'string') {
      console.warn('Invalid address provided');
      return;
    }

    const addressElement = document.querySelector('#address');
    const footerAddressElement = document.querySelector('#footerAddress');

    const addressHTML = `<a href="https://www.google.com/maps?q=${encodeURIComponent(
      a
    )}" target="_blank">${a}</a>`;

    if (addressElement) {
      addressElement.innerHTML = addressHTML;
    }
    if (footerAddressElement) {
      footerAddressElement.innerHTML = addressHTML;
    }
  } catch (err) {
    console.error('Error in address function:', err);
  }
};

const googlePlaceData = async () => {
  try {
    // Try to get Google Places data same as inventory page
    if (typeof googlePlaceDataAPI === 'function') {
      return await googlePlaceDataAPI();
    } else {
      console.warn('googlePlaceDataAPI function not available');
      return null;
    }
  } catch (err) {
    console.warn('Error in googlePlaceData:', err);
    return null;
  }
};

// Fallback function in case Google Places API is not available
const setDefaultBusinessInfo = () => {
  // Set fallback business information
  const phoneNumberElement = document.querySelector('#phoneNumber');
  const footerPhoneElement = document.querySelector('#footerPhoneNumber');
  const addressElement = document.querySelector('#address');
  const footerAddressElement = document.querySelector('#footerAddress');
  const currentDayElement = document.querySelector('#currentDayHours');
  const workingHoursElement = document.querySelector('#workingHours');

  if (phoneNumberElement) {
    phoneNumberElement.innerHTML =
      '<a href="tel:9054630928">(905) 463-0928</a>';
  }
  if (footerPhoneElement) {
    footerPhoneElement.innerHTML =
      '<a href="tel:9054630928">(905) 463-0928</a>';
  }
  if (addressElement) {
    addressElement.innerHTML =
      '<a href="https://www.google.com/maps?q=5+Hansen+Rd+S,+Brampton,+ON+L6W+3H6" target="_blank">5 Hansen Rd S, Brampton, ON L6W 3H6</a>';
  }
  if (footerAddressElement) {
    footerAddressElement.innerHTML =
      '<a href="https://www.google.com/maps?q=5+Hansen+Rd+S,+Brampton,+ON+L6W+3H6" target="_blank">5 Hansen Rd S, Brampton, ON L6W 3H6</a>';
  }
  if (currentDayElement) {
    currentDayElement.innerHTML = 'Mon-Fri 9AM-6PM, Sat 9AM-5PM';
  }
  if (workingHoursElement) {
    workingHoursElement.innerHTML = `
      <li>Monday: 9:00 AM – 6:00 PM</li>
      <li>Tuesday: 9:00 AM – 6:00 PM</li>
      <li>Wednesday: 9:00 AM – 6:00 PM</li>
      <li>Thursday: 9:00 AM – 6:00 PM</li>
      <li>Friday: 9:00 AM – 6:00 PM</li>
      <li>Saturday: 9:00 AM – 5:00 PM</li>
      <li>Sunday: Closed</li>
    `;
  }
};

// Enhanced reCAPTCHA implementation for Car Finder form
// Add this to your main.js file or include it separately

// Function to check if reCAPTCHA is loaded
const isRecaptchaLoaded = () => {
  return typeof grecaptcha !== 'undefined' && grecaptcha.ready;
};

// Function to execute reCAPTCHA and submit form
const executeRecaptchaAndSubmit = async (formData) => {
  return new Promise((resolve, reject) => {
    if (!isRecaptchaLoaded()) {
      reject(new Error('reCAPTCHA not loaded'));
      return;
    }

    grecaptcha.ready(function () {
      grecaptcha
        .execute('6LcJjOEiAAAAAIe7TA3cT8nT-xMZXKKBFtbaDrs4', {
          action: 'carfinder',
        })
        .then(async function (token) {
          try {
            // Prepare the payload
            const payload = {
              data: {
                fullName: formData.fullName,
                email: formData.email,
                phone: formData.phone,
                contactPreference: formData.contactPreference,
                bestTimeToContact: formData.bestTimeToContact,
                timeline: formData.timeline,
                minBudget: parseInt(formData.minBudget),
                maxBudget: parseInt(formData.maxBudget),
                bodyTypes: formData.bodyTypes,
                preferredMakes: formData.preferredMakes,
                yearRange: formData.yearRange,
                maxMileage: formData.maxMileage
                  ? parseInt(formData.maxMileage)
                  : null,
                transmission: formData.transmission,
                desiredFeatures: formData.desiredFeatures,
                needFinancing: formData.needFinancing,
                haveTradeIn: formData.haveTradeIn,
                additionalNotes: formData.additionalNotes,
                newsletterSignup: formData.newsletterSignup,
                submissionDate: formData.submissionDate,
                referrer: formData.referrer,
                status: 'pending',
                token: token, // reCAPTCHA token
              },
            };

            // Submit to CarFinderController using axios
            const response = await breq.post(
              '/CarFinder/CarFinderAsync',
              payload
            );
            resolve(response.data);
          } catch (error) {
            reject(error);
          }
        })
        .catch((error) => {
          reject(new Error('reCAPTCHA execution failed: ' + error.message));
        });
    });
  });
};

// Enhanced submit form function with better error handling
const submitForm = async () => {
  console.log('Submit form called');

  // Validate final step
  if (!validateCurrentStep()) {
    return;
  }

  const submitBtn = document.getElementById('nextBtn');
  const originalText = submitBtn.innerHTML;

  try {
    // Show loading state
    submitBtn.innerHTML =
      '<i class="ion-load-c animate-spin"></i> Submitting...';
    submitBtn.disabled = true;

    const formData = collectFormData();

    // Check if reCAPTCHA is available
    if (!isRecaptchaLoaded()) {
      throw new Error(
        'Security verification is not available. Please refresh the page and try again.'
      );
    }

    // Execute reCAPTCHA and submit
    const result = await executeRecaptchaAndSubmit(formData);

    console.log('Car finder request submitted successfully:', result);

    // Show success message
    showSuccessMessage();

    // Optional: Send to Google Analytics
    if (typeof gtag !== 'undefined') {
      gtag('event', 'car_finder_submission', {
        event_category: 'engagement',
        event_label: 'car_finder_form',
        value: 1,
      });
    }
  } catch (error) {
    console.error('Form submission error:', error);

    // Provide user-friendly error messages for axios errors
    let errorMessage =
      'There was an error submitting your request. Please try again or call us at (905) 463-0928.';

    if (error.response) {
      // Server responded with error status
      if (error.response.status === 403) {
        errorMessage = 'Security verification failed. Please try again.';
      } else if (error.response.status >= 500) {
        errorMessage =
          'Server error. Please try again in a moment or call us at (905) 463-0928.';
      } else if (error.response.data && error.response.data.message) {
        errorMessage = error.response.data.message;
      }
    } else if (error.request) {
      // Network error
      errorMessage =
        'Network error. Please check your connection and try again.';
    } else if (error.message.includes('reCAPTCHA')) {
      errorMessage =
        'Security verification failed. Please refresh the page and try again.';
    }

    alert(errorMessage);
  } finally {
    // Restore button state
    submitBtn.innerHTML = originalText;
    submitBtn.disabled = false;
  }
};

// Function to ensure reCAPTCHA is loaded before form submission
const ensureRecaptchaLoaded = () => {
  return new Promise((resolve, reject) => {
    if (isRecaptchaLoaded()) {
      resolve();
      return;
    }

    // Wait for reCAPTCHA to load
    let attempts = 0;
    const maxAttempts = 50; // 5 seconds max wait

    const checkInterval = setInterval(() => {
      attempts++;

      if (isRecaptchaLoaded()) {
        clearInterval(checkInterval);
        resolve();
      } else if (attempts >= maxAttempts) {
        clearInterval(checkInterval);
        reject(new Error('reCAPTCHA failed to load'));
      }
    }, 100);
  });
};

// Initialize reCAPTCHA check when form is ready
const initializeRecaptcha = async () => {
  try {
    await ensureRecaptchaLoaded();
    console.log('✅ reCAPTCHA loaded successfully');
  } catch (error) {
    console.error('❌ reCAPTCHA failed to load:', error);
    // You might want to show a user message or disable the form
  }
};

// Call this after your form is initialized
document.addEventListener('DOMContentLoaded', () => {
  // Initialize reCAPTCHA after a short delay to ensure script is loaded
  setTimeout(initializeRecaptcha, 1000);
});
